import { useState } from 'react';
import dayjs from 'dayjs';
import { IMaskInput } from 'react-imask';
import { Field, FieldLabel, FormField, FormModal, FormRow, SearchSelect, Select } from '../../components';
import { assigneeName, useCreateBookingMutation } from '../../queries/scheduling';
import { DURATION_OPTIONS } from './constants';
import { DEFAULT_TZ } from './time';

export default function NewBookingModal({ type, assignees, tz, initial, onClose }) {
  const block = type === 'block';
  const [form, setForm] = useState({ start: '', durationMin: 60, name: '', phone: '', notes: '', assignedTo: '', ...initial });
  const create = useCreateBookingMutation();
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const valid = Boolean(form.start) && (block || form.name.trim());

  const submit = () =>
    create.mutate(
      {
        type,
        start: dayjs.tz(form.start, tz || DEFAULT_TZ).toISOString(),
        durationMin: block ? Number(form.durationMin) : undefined,
        contact: block ? undefined : { name: form.name.trim(), phone: form.phone.trim() },
        notes: form.notes.trim(),
        assignedTo: form.assignedTo || null
      },
      { onSuccess: onClose }
    );

  return (
    <FormModal
      title={block ? 'Block time' : 'New tour'}
      subtitle={block ? 'Nobody can book this slot' : 'Add a tour to the leasing calendar'}
      okText={block ? 'Block time' : 'Book tour'}
      valid={valid}
      loading={create.isPending}
      onSubmit={submit}
      onClose={onClose}
    >
      <FormRow>
        <FormField>
          <FieldLabel htmlFor="booking-start">Starts</FieldLabel>
          <Field id="booking-start" type="datetime-local" value={form.start} onChange={set('start')} required />
        </FormField>
        {block && (
          <FormField>
            <FieldLabel htmlFor="booking-duration">Length</FieldLabel>
            <Select id="booking-duration" value={form.durationMin} onChange={set('durationMin')}>
              {[...DURATION_OPTIONS, 120, 240, 480].map((m) => (
                <option key={m} value={m}>
                  {m < 60 ? `${m} min` : `${m / 60} hr`}
                </option>
              ))}
            </Select>
          </FormField>
        )}
      </FormRow>
      {!block && (
        <FormRow>
          <FormField>
            <FieldLabel htmlFor="booking-name">Contact name</FieldLabel>
            <Field id="booking-name" value={form.name} onChange={set('name')} required />
          </FormField>
          <FormField>
            <FieldLabel htmlFor="booking-phone">Phone</FieldLabel>
            <Field
              as={IMaskInput}
              id="booking-phone"
              type="tel"
              mask="(000) 000-0000"
              placeholder="(555) 555-0100"
              value={form.phone}
              onAccept={(phone) => setForm((f) => ({ ...f, phone }))}
            />
          </FormField>
        </FormRow>
      )}
      <FormField>
        <FieldLabel htmlFor="booking-agent-new">Agent</FieldLabel>
        <SearchSelect
          id="booking-agent-new"
          allowClear
          placeholder={block ? 'Whole team' : 'Unassigned'}
          value={form.assignedTo}
          onChange={(v) => setForm((f) => ({ ...f, assignedTo: v }))}
          options={assignees.map((a) => ({ value: a._id, label: assigneeName(a) }))}
        />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="booking-notes">{block ? 'Reason' : 'Notes'}</FieldLabel>
        <Field id="booking-notes" value={form.notes} onChange={set('notes')} placeholder={block ? 'e.g. Team meeting' : 'Unit, access notes…'} />
      </FormField>
    </FormModal>
  );
}
